import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getComplianceShipments } from '../../api';
import { Loader2, Calendar, ChevronDown, ChevronUp, CheckCircle, XCircle, AlertCircle, Search, Filter, FilterX, Download } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

// Colores para los gráficos
const COLORS = {
  onTime: '#10b981', // Verde para entregas a tiempo
  delayed: '#ef4444', // Rojo para entregas con retraso
  notDelivered: '#6b7280', // Gris para no entregados
};

const PickupComplianceDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [complianceData, setComplianceData] = useState({
    onTime: 0,
    delayed: 0,
    notDelivered: 0,
  });
  const [shipmentData, setShipmentData] = useState([]);
  const [processedShipments, setProcessedShipments] = useState([]);
  const [allProcessedShipments, setAllProcessedShipments] = useState([]); // Para mantener todos los embarques sin filtrar
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: 'reference',
    direction: 'asc',
  });
  
  // Estados para los filtros adicionales
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    pickup_date_range: [null, null], // Rango para fecha de carga
    delivery_date_range: [null, null], // Rango para fecha de entrega (cita)
    customer: [], // Cliente (que antes era "vendor")
    Lt: [], // Línea de transporte
    status: [], // Estado del embarque
    compliance: []  // Añadir filtro de cumplimiento
  });
  const [filterOptions, setFilterOptions] = useState({
    customers: [], // Lista de clientes disponibles
    carriers: [], // Lista de transportistas disponibles
    statuses: [], // Lista de estados disponibles
    compliances: ['A Tiempo', 'Con Retraso', 'Pendiente']  // Opciones de cumplimiento
  });
  const [openDropdowns, setOpenDropdowns] = useState({
    customers: false,
    carriers: false,
    statuses: false,
    compliances: false  // Añadir dropdown para cumplimiento
  });
  
  // Referencia para el formulario de filtros
  const filterFormRef = useRef(null);

  // Estados para las pestañas de detalles
  const [activeTab, setActiveTab] = useState('client');
  
  // Estados para almacenar datos procesados por tipo
  const [complianceByClient, setComplianceByClient] = useState([]);
  const [complianceByCarrier, setComplianceByCarrier] = useState([]); // Por línea transportista
  const [complianceByFinalClient, setComplianceByFinalClient] = useState([]); // Por cliente final

  // Función para obtener los datos y procesarlos
  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Obtener todos los embarques sin aplicar filtros
      const data = await getComplianceShipments();
      setShipmentData(data);

      // Procesar datos para análisis de cumplimiento
      const processed = processComplianceData(data);

      // Guardar la lista completa de embarques procesados
      setAllProcessedShipments(processed);
      setProcessedShipments(processed); // Mostrar todos los embarques desde el inicio

      // Extraer las opciones de filtro
      extractFilterOptions(processed);
    } catch (error) {
      console.error('Error al obtener datos para el dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Ajustar las variables y lógica para enfocarse en las citas de carga
  const processComplianceData = (shipments) => {
    let onTimeCount = 0;
    let delayedCount = 0;
    let notDeliveredCount = 0;

    const clientComplianceMap = {};
    const carrierComplianceMap = {};
    const finalClientComplianceMap = {};    const processedShipmentsList = shipments.map((shipment) => {
      // Extraer la fecha real para la orden con loading_order = 1
      const actualDate = shipment.orders?.find(order => order.loading_order === 1)?.metrics?.order_OriginATA || null;

      // Calcular tiempo de retraso
      const delayTime = actualDate && shipment.pickup_date
        ? Math.max(0, new Date(actualDate) - new Date(shipment.pickup_date))
        : null;

      // Determinar el estado de cumplimiento basado en el tiempo de retraso
      const complianceStatus = delayTime > 0 ? 'delayed' : 'onTime';
      
      // Añadir información del camión
      const truck = shipment.Truck || 'N/A';

      // Actualizar contadores de cumplimiento
      if (complianceStatus === 'onTime') {
        onTimeCount++;
      } else {
        delayedCount++;
      }

      // Actualizar mapas de cumplimiento
      const clientName = shipment.vendor || 'Unknown';
      if (!clientComplianceMap[clientName]) {
        clientComplianceMap[clientName] = { onTime: 0, delayed: 0, total: 0 };
      }
      clientComplianceMap[clientName][complianceStatus]++;
      clientComplianceMap[clientName].total++;

      const carrierName = shipment.Lt || 'Unspecified';
      if (!carrierComplianceMap[carrierName]) {
        carrierComplianceMap[carrierName] = { onTime: 0, delayed: 0, total: 0 };
      }
      carrierComplianceMap[carrierName][complianceStatus]++;
      carrierComplianceMap[carrierName].total++;

      const finalClientName = shipment.customer || 'Unspecified';
      if (!finalClientComplianceMap[finalClientName]) {
        finalClientComplianceMap[finalClientName] = { onTime: 0, delayed: 0, total: 0 };
      }
      finalClientComplianceMap[finalClientName][complianceStatus]++;
      finalClientComplianceMap[finalClientName].total++;

      // Formatear tiempo de retraso
      const formattedDelayTime = delayTime !== null
        ? `${Math.floor(delayTime / (1000 * 60 * 60))}h ${Math.floor((delayTime % (1000 * 60 * 60)) / (1000 * 60))}m`
        : 'Sin retraso';

      console.log('Processing shipment:', {
        reference: shipment.reference,
        pickup_date: shipment.pickup_date,
        actualDate,
        delayTime,
      });      return {
        ...shipment,
        complianceStatus, // Actualizar el estado de cumplimiento
        actualDate,
        delayTime,  // Añadir el tiempo de retraso al embarque procesado
        truck      // Añadir el camión al embarque procesado
      };
    });

    setComplianceData({
      onTime: onTimeCount,
      delayed: delayedCount,
      notDelivered: notDeliveredCount,
    });

    return processedShipmentsList;
  };

  // Función para extraer las opciones de filtro de los embarques
  const extractFilterOptions = (shipments) => {
    if (!shipments || shipments.length === 0) return;
    
    // Extraer clientes (vendors) únicos
    const uniqueCustomers = Array.from(
      new Set(
        shipments
          .filter(s => s.vendor && s.vendor !== 'N/A')
          .map(s => s.vendor)
      )
    ).sort();
    
    // Extraer transportistas (líneas de transporte) únicos
    const uniqueCarriers = Array.from(
      new Set(
        shipments
          .filter(s => s.Lt && s.Lt !== 'N/A')
          .map(s => s.Lt)
      )
    ).sort();
    
    // Extraer estados únicos
    const uniqueStatuses = Array.from(
      new Set(
        shipments
          .filter(s => s.status)
          .map(s => s.status)
      )
    ).sort();
    
    // Actualizar opciones de filtro manteniendo las opciones de cumplimiento existentes
    setFilterOptions(prevOptions => ({
      customers: uniqueCustomers,
      carriers: uniqueCarriers,
      statuses: uniqueStatuses,
      compliances: prevOptions.compliances || ['A Tiempo', 'Con Retraso', 'Pendiente']
    }));
  };

  // Efecto para cargar datos iniciales - modificado para no depender del dateRange
  useEffect(() => {
    fetchData();
  }, []); // Eliminado dateRange de las dependencias para mostrar todos los viajes inicialmente
  
  // Ajustar la lógica de filtros para enfocarse en las citas de carga
  const applyFilters = () => {
    let results = [...allProcessedShipments];

    // Filtrar por cliente (vendor)
    if (filters.customer.length > 0) {
      results = results.filter((shipment) => {
        const vendor = shipment.vendor || '';
        return filters.customer.includes(vendor);
      });
    }

    // Filtrar por línea transportista (Lt)
    if (filters.Lt.length > 0) {
      results = results.filter((shipment) => {
        const lt = shipment.Lt || '';
        return filters.Lt.includes(lt);
      });
    }

    // Filtrar por status
    if (filters.status.length > 0) {
      results = results.filter((shipment) => filters.status.includes(shipment.status));
    }

    // Filtrar por cumplimiento (A Tiempo, Con Retraso)
    if (filters.compliance.length > 0) {
      results = results.filter((shipment) => {
        // Convertir el estado de cumplimiento interno (onTime, delayed) a los valores de la tabla
        const displayComplianceStatus = shipment.complianceStatus === 'onTime' ? 'A Tiempo' : 'Con Retraso';
        return filters.compliance.includes(displayComplianceStatus);
      });
    }

    // Filtrar por fecha de carga
    if (filters.pickup_date_range[0] && filters.pickup_date_range[1]) {
      const startDate = new Date(filters.pickup_date_range[0]);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(filters.pickup_date_range[1]);
      endDate.setHours(23, 59, 59, 999);

      results = results.filter((s) => {
        if (!s.pickup_date) return false;
        const dateToCompare = new Date(s.pickup_date);
        return dateToCompare >= startDate && dateToCompare <= endDate;
      });
    }

    // Actualizar los datos de las tarjetas y gráficos con los resultados filtrados
    updateComplianceData(results);
    
    // Actualizar los embarques procesados
    setProcessedShipments(results);
  };
  
  // Función para actualizar los datos de las tarjetas y gráficos con los datos filtrados
  const updateComplianceData = (filteredResults) => {
    let onTimeCount = 0;
    let delayedCount = 0;
    let notDeliveredCount = 0;
    
    // Contar los embarques por tipo de cumplimiento
    filteredResults.forEach(shipment => {
      const status = shipment.complianceStatus || '';
      
      if (status === 'onTime') {
        onTimeCount++;
      } else if (status === 'delayed') {
        delayedCount++;
      } else {
        // Si no tiene un estado válido, contar como no entregado
        notDeliveredCount++;
      }
    });
    
    // Actualizar los datos de cumplimiento para las tarjetas y gráficos
    setComplianceData({
      onTime: onTimeCount,
      delayed: delayedCount,
      notDelivered: notDeliveredCount,
    });
    
    // También podríamos actualizar aquí los datos para las pestañas de detalles
    // si es necesario mostrar información desglosada por cliente, transportista, etc.
    updateDetailedComplianceData(filteredResults);
  };
  
  // Función para actualizar los datos detallados por cliente, transportista, etc.
  const updateDetailedComplianceData = (filteredResults) => {
    const clientComplianceMap = {};
    const carrierComplianceMap = {};
    const finalClientComplianceMap = {};
    
    // Procesar cada embarque filtrado para actualizar los mapas de cumplimiento
    filteredResults.forEach(shipment => {
      const complianceStatus = shipment.complianceStatus || 'onTime'; // Valor por defecto si no existe
      
      // Actualizar mapa de cumplimiento por cliente (vendor)
      const clientName = shipment.vendor || 'Unknown';
      if (!clientComplianceMap[clientName]) {
        clientComplianceMap[clientName] = { onTime: 0, delayed: 0, total: 0 };
      }
      clientComplianceMap[clientName][complianceStatus]++;
      clientComplianceMap[clientName].total++;
      
      // Actualizar mapa de cumplimiento por transportista (Lt)
      const carrierName = shipment.Lt || 'Unspecified';
      if (!carrierComplianceMap[carrierName]) {
        carrierComplianceMap[carrierName] = { onTime: 0, delayed: 0, total: 0 };
      }
      carrierComplianceMap[carrierName][complianceStatus]++;
      carrierComplianceMap[carrierName].total++;
      
      // Actualizar mapa de cumplimiento por cliente final (customer)
      const finalClientName = shipment.customer || 'Unspecified';
      if (!finalClientComplianceMap[finalClientName]) {
        finalClientComplianceMap[finalClientName] = { onTime: 0, delayed: 0, total: 0 };
      }
      finalClientComplianceMap[finalClientName][complianceStatus]++;
      finalClientComplianceMap[finalClientName].total++;
    });
    
    // Convertir los mapas a arrays para las gráficas
    const clientComplianceArray = Object.entries(clientComplianceMap).map(([name, data]) => ({
      name,
      onTime: data.onTime || 0,
      delayed: data.delayed || 0,
      total: data.total || 0,
      complianceRate: data.total > 0 ? ((data.onTime / data.total) * 100).toFixed(1) : "0.0",
      onTimeRate: data.total > 0 ? (data.onTime / data.total) * 100 : 0 // Añadido para calcular el porcentaje de cumplimiento
    })).sort((a, b) => b.total - a.total);
    
    const carrierComplianceArray = Object.entries(carrierComplianceMap).map(([name, data]) => ({
      name,
      onTime: data.onTime || 0,
      delayed: data.delayed || 0,
      total: data.total || 0,
      complianceRate: data.total > 0 ? ((data.onTime / data.total) * 100).toFixed(1) : "0.0",
      onTimeRate: data.total > 0 ? (data.onTime / data.total) * 100 : 0 // Añadido para calcular el porcentaje de cumplimiento
    })).sort((a, b) => b.total - a.total);
    
    const finalClientComplianceArray = Object.entries(finalClientComplianceMap).map(([name, data]) => ({
      name,
      onTime: data.onTime || 0,
      delayed: data.delayed || 0,
      total: data.total || 0,
      complianceRate: data.total > 0 ? ((data.onTime / data.total) * 100).toFixed(1) : "0.0",
      onTimeRate: data.total > 0 ? (data.onTime / data.total) * 100 : 0 // Añadido para calcular el porcentaje de cumplimiento
    })).sort((a, b) => b.total - a.total);
    
    // Actualizar los estados con los nuevos datos procesados
    setComplianceByClient(clientComplianceArray);
    setComplianceByCarrier(carrierComplianceArray);
    setComplianceByFinalClient(finalClientComplianceArray);
  };

  // Manejar el envío del formulario de filtros
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    applyFilters();
    setShowFilters(false);
    setCurrentPage(1); // Volver a la primera página
  };
  
  // Restablecer los filtros
  const handleResetFilters = () => {
    // Restablecer los filtros a sus valores iniciales
    setFilters({
      pickup_date_range: [null, null],
      delivery_date_range: [null, null],
      customer: [],
      Lt: [],
      status: [],
      compliance: []  // Restablecer filtro de cumplimiento
    });
    
    // Restaurar la lista completa de embarques sin filtrar
    setProcessedShipments(allProcessedShipments);
    
    // Actualizar los datos de cumplimiento con todos los embarques
    updateComplianceData(allProcessedShipments);
    
    // Cerrar el panel de filtros y volver a la primera página
    setShowFilters(false);
    setCurrentPage(1);
  };
  
  // Alternar estados de dropdowns
  const toggleDropdown = (dropdown) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
  };
  
  // Manejar cambios en los filtros de selección múltiple
  const handleMultiSelectChange = (name, value) => {
    setFilters(prev => {
      // Si ya está seleccionado, eliminarlo
      if (prev[name].includes(value)) {
        return {
          ...prev,
          [name]: prev[name].filter(item => item !== value),
        };
      } else {
        // Si no está seleccionado, agregarlo
        return {
          ...prev,
          [name]: [...prev[name], value],
        };
      }
    });
    // No llamar a applyFilters aquí, para que los filtros solo se apliquen cuando se presione el botón
  };

  // Manejar "Seleccionar todo" o "Deseleccionar todo" para un filtro
  const handleSelectAllChange = (name, values) => {
    setFilters(prev => {
      // Si ya están todos seleccionados, deseleccionar todos
      if (prev[name].length === values.length) {
        return {
          ...prev,
          [name]: [],
        };
      } else {
        // Si no están todos seleccionados, seleccionar todos
        return {
          ...prev,
          [name]: [...values],
        };
      }
    });
    // No llamar a applyFilters aquí, para que los filtros solo se apliquen cuando se presione el botón
  };

  // Función para manejar ordenación
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Función para formatear fechas
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Formatear tiempo de retraso para la UI
  const formatDelayTime = (ms) => {
    if (!ms) return 'N/A';
    
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Formatear tiempo de retraso para Excel (formato HH:MM)
  const formatDelayTimeForExcel = (ms) => {
    if (!ms) return '00:00';
    
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    
    // Asegurar formato de 2 dígitos
    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');
    
    return `${formattedHours}:${formattedMinutes}`;
  };

  // Verificar si hay algún filtro activo
  const isFilterActive = filters.pickup_date_range[0] || 
                         filters.delivery_date_range[0] || 
                         filters.customer.length > 0 || 
                         filters.Lt.length > 0 || 
                         filters.status.length > 0 || 
                         filters.compliance.length > 0;

  // Filtrar y ordenar embarques para la tabla
  const filteredShipments = processedShipments
    .filter(shipment => {
      const searchLower = searchTerm.toLowerCase();
        // Solo aplicar filtro de búsqueda por texto, ya que los otros filtros
      // se aplican en la función applyFilters
      return (
        (shipment.reference && shipment.reference.toLowerCase().includes(searchLower)) ||
        (shipment.customer && shipment.customer.toLowerCase().includes(searchLower)) ||
        (shipment.vendor && shipment.vendor.toLowerCase().includes(searchLower)) ||
        (shipment.truck && shipment.truck.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => {
      const { key, direction } = sortConfig;
      
      // Manejar valores nulos o indefinidos
      if (a[key] === null) return direction === 'asc' ? -1 : 1;
      if (b[key] === null) return direction === 'asc' ? 1 : -1;
      
      // Para fechas
      if (key === 'scheduledDate' || key === 'actualDate' || key === 'pickup_date') {
        return direction === 'asc'
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      
      // Para strings
      if (typeof a[key] === 'string') {
        return direction === 'asc'
          ? a[key].localeCompare(b[key])
          : b[key].localeCompare(a[key]);
      }
      
      // Para números
      return direction === 'asc' ? a[key] - b[key] : b[key] - a[key];
    });

  // Paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredShipments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredShipments.length / itemsPerPage);

  // Calcular tasa de cumplimiento general
  const totalShipments = complianceData.onTime + complianceData.delayed + complianceData.notDelivered || 0;
  const complianceRate = (complianceData.onTime || 0) > 0 && (complianceData.onTime + complianceData.delayed) > 0 
    ? ((complianceData.onTime / (complianceData.onTime + complianceData.delayed)) * 100).toFixed(1) 
    : "0.0";
  // Preparar datos para el gráfico de pie
  const pieChartData = [
    { name: 'A Tiempo', value: complianceData.onTime || 0, color: COLORS.onTime },
    { name: 'Con Retraso', value: complianceData.delayed || 0, color: COLORS.delayed },
    { name: 'No Entregados', value: complianceData.notDelivered || 0, color: COLORS.notDelivered },
  ];
  
  // Función para obtener los datos según la pestaña activa
  const getActiveTabData = () => {
    switch (activeTab) {
      case 'client':
        return complianceByClient;
      case 'carrier':
        return complianceByCarrier;
      case 'finalClient':
        return complianceByFinalClient;
      default:
        return complianceByClient;
    }
  };

  // Obtener el título según la pestaña activa
  const getActiveTabTitle = () => {
    switch (activeTab) {
      case 'client':
        return 'Cliente';
      case 'carrier':
        return 'Línea Transportista';
      case 'finalClient':
        return 'Cliente Final';
      default:
        return 'Cliente';
    }
  };

  // Función para exportar tabla a Excel
  const handleExportExcel = () => {
    if (!filteredShipments || filteredShipments.length === 0) {
      return;
    }

    try {      // Preparar datos para Excel con los campos de la tabla
      const exportData = filteredShipments.map(shipment => ({
        'Referencia': shipment.reference || 'N/A',
        'Truck': shipment.truck || 'N/A',
        'Cliente': shipment.vendor || 'N/A',
        'Cliente Final': shipment.customer || 'N/A', 
        'L. Transporte': shipment.Lt || 'N/A',
        'Estado': shipment.status || 'N/A',
        'Cumplimiento': shipment.complianceStatus === 'onTime' 
          ? 'A Tiempo' 
          : shipment.complianceStatus === 'delayed' 
            ? 'Con Retraso' 
            : 'Pendiente',
        'Fecha Programada': shipment.pickup_date 
          ? new Date(shipment.pickup_date).toLocaleString('es-MX', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit'
            }) 
          : 'N/A',        'Fecha Real': shipment.actualDate 
          ? new Date(shipment.actualDate).toLocaleString('es-MX', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit'
            })
          : 'N/A',
        'Tiempo de Retraso': shipment.delayTime 
          ? formatDelayTimeForExcel(shipment.delayTime) 
          : '00:00',
        'Órdenes': shipment.orderCount || 0
      }));

      // Crear hoja de trabajo
      const worksheet = XLSX.utils.json_to_sheet(exportData);      // Configurar anchos de columna
      const columnWidths = [
        { wch: 15 }, // Referencia
        { wch: 12 }, // Truck
        { wch: 20 }, // Cliente
        { wch: 20 }, // Cliente Final
        { wch: 15 }, // L. Transporte
        { wch: 12 }, // Estado
        { wch: 15 }, // Cumplimiento
        { wch: 25 }, // Fecha Programada
        { wch: 25 }, // Fecha Real
        { wch: 15 }, // Tiempo de Retraso
        { wch: 10 }  // Órdenes
      ];
      
      worksheet['!cols'] = columnWidths;

      // Crear libro y agregar hoja
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Cargas');

      // Generar archivo Excel con fecha actual
      const today = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `Reporte_Cumplimiento_Cargas_${today}.xlsx`);
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
    }
  };

  // Renderizado del componente
  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <Calendar className="mr-2 text-blue-600" size={24} />
          Dashboard de Cumplimiento de Carga
        </h2>
          {/* Botones de acción */}
        <div className="flex space-x-2">          {/* Botón de filtro */}
          <button 
            onClick={() => setShowFilters(!showFilters)} 
            className={`flex items-center space-x-2 px-4 py-2 rounded-md ${
              isFilterActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {isFilterActive ? (
              <FilterX size={18} />
            ) : (
              <Filter size={18} />            )}
            <span>Filtros</span>
            {isFilterActive && (
              <div className="bg-white text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs">
                {(filters.pickup_date_range[0] ? 1 : 0) + 
                 (filters.delivery_date_range[0] ? 1 : 0) + 
                 (filters.customer.length > 0 ? 1 : 0) + 
                 (filters.Lt.length > 0 ? 1 : 0) +
                 (filters.status.length > 0 ? 1 : 0) +
                 (filters.compliance.length > 0 ? 1 : 0)}
              </div>
            )}
          </button>
          
          {/* Botón para exportar a Excel */}
          <button 
            onClick={handleExportExcel}
            className="flex items-center space-x-2 px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700"
            disabled={isLoading || filteredShipments.length === 0}
          >
            <Download size={18} />
            <span>Exportar Excel</span>
          </button>
        </div>
      </div>
      
      {/* Panel de filtros */}
      {showFilters && (
        <div className="bg-white p-4 rounded-md shadow mb-6">
          <form ref={filterFormRef} onSubmit={handleFilterSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Fecha de carga */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de carga</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 z-10">
                    <Calendar size={16} className="text-gray-400" />
                  </span>
                  <DatePicker
                    selected={filters.pickup_date_range[0] ? new Date(filters.pickup_date_range[0]) : null}
                    onChange={(dates) => {
                      const [start, end] = dates;
                      const startDate = start ? new Date(start) : null;
                      const endDate = end ? new Date(end) : null;
                      setFilters(prev => ({ 
                        ...prev, 
                        pickup_date_range: [startDate, endDate] 
                      }));
                    }}
                    startDate={filters.pickup_date_range[0] ? new Date(filters.pickup_date_range[0]) : null}
                    endDate={filters.pickup_date_range[1] ? new Date(filters.pickup_date_range[1]) : null}
                    selectsRange
                    placeholderText="Seleccionar rango de fechas"
                    className="w-full pl-10 rounded-md border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
              </div>
              
              {/* Fecha de entrega (cita) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de entrega</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 z-10">
                    <Calendar size={16} className="text-gray-400" />
                  </span>
                  <DatePicker
                    selected={filters.delivery_date_range[0] ? new Date(filters.delivery_date_range[0]) : null}
                    onChange={(dates) => {
                      const [start, end] = dates;
                      const startDate = start ? new Date(start) : null;
                      const endDate = end ? new Date(end) : null;
                      setFilters(prev => ({ 
                        ...prev, 
                        delivery_date_range: [startDate, endDate] 
                      }));
                    }}
                    startDate={filters.delivery_date_range[0] ? new Date(filters.delivery_date_range[0]) : null}
                    endDate={filters.delivery_date_range[1] ? new Date(filters.delivery_date_range[1]) : null}
                    selectsRange
                    placeholderText="Seleccionar rango de fechas"
                    className="w-full pl-10 rounded-md border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
              </div>
            </div>
            
            {/* Filtros de selección múltiple */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Cliente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clientes
                  <span className="ml-1 text-xs text-gray-500">
                    ({filters.customer.length} seleccionados)
                  </span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => toggleDropdown('customers')}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-1 focus:ring-blue-500 flex justify-between items-center"
                  >
                    <span className="block truncate">
                      {filters.customer.length === 0 ? 'Seleccionar clientes' : 
                       filters.customer.length === filterOptions.customers.length ? 'Todos los clientes' :
                       `${filters.customer.length} cliente(s) seleccionado(s)`}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    </span>
                  </button>
                  
                  {openDropdowns.customers && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                      <div className="p-2 border-b border-gray-200 bg-gray-50 sticky top-0">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={filterOptions.customers.length > 0 && filters.customer.length === filterOptions.customers.length}
                            onChange={() => handleSelectAllChange('customer', filterOptions.customers.map(c => c))}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium">
                            {filterOptions.customers.length > 0 && filters.customer.length === filterOptions.customers.length 
                              ? "Deseleccionar todos" 
                              : "Seleccionar todos"}
                          </span>
                        </label>
                      </div>
                      <div className="py-1 px-2">
                        {filterOptions.customers.length === 0 ? (
                          <p className="text-sm text-gray-500 italic p-2">No hay clientes disponibles</p>
                        ) : (
                          <div className="max-h-40 overflow-auto">
                            {filterOptions.customers.map(customer => (
                              <label key={customer} className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 w-full">
                                <input
                                  type="checkbox"
                                  checked={filters.customer.includes(customer)}
                                  onChange={() => handleMultiSelectChange('customer', customer)}
                                  className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm truncate">{customer}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Línea de transporte */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Línea de transporte
                  <span className="ml-1 text-xs text-gray-500">
                    ({filters.Lt.length} seleccionados)
                  </span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => toggleDropdown('carriers')}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-1 focus:ring-blue-500 flex justify-between items-center"
                  >
                    <span className="block truncate">
                      {filters.Lt.length === 0 ? 'Seleccionar transportistas' : 
                       filters.Lt.length === filterOptions.carriers.length ? 'Todos los transportistas' :
                       `${filters.Lt.length} transportista(s) seleccionado(s)`}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    </span>
                  </button>
                  
                  {openDropdowns.carriers && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                      <div className="p-2 border-b border-gray-200 bg-gray-50 sticky top-0">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={filterOptions.carriers.length > 0 && filters.Lt.length === filterOptions.carriers.length}
                            onChange={() => handleSelectAllChange('Lt', filterOptions.carriers.map(c => c))}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium">
                            {filterOptions.carriers.length > 0 && filters.Lt.length === filterOptions.carriers.length 
                              ? "Deseleccionar todos" 
                              : "Seleccionar todos"}
                          </span>
                        </label>
                      </div>
                      <div className="py-1 px-2">
                        {filterOptions.carriers.length === 0 ? (
                          <p className="text-sm text-gray-500 italic p-2">No hay transportistas disponibles</p>
                        ) : (
                          <div className="max-h-40 overflow-auto">
                            {filterOptions.carriers.map(carrier => (
                              <label key={carrier} className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 w-full">
                                <input
                                  type="checkbox"
                                  checked={filters.Lt.includes(carrier)}
                                  onChange={() => handleMultiSelectChange('Lt', carrier)}
                                  className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm truncate">{carrier}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Estado */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estados
                  <span className="ml-1 text-xs text-gray-500">
                    ({filters.status.length} seleccionados)
                  </span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => toggleDropdown('statuses')}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-1 focus:ring-blue-500 flex justify-between items-center"
                  >
                    <span className="block truncate">
                      {filters.status.length === 0 ? 'Seleccionar estados' : 
                       filters.status.length === filterOptions.statuses.length ? 'Todos los estados' :
                       `${filters.status.length} estado(s) seleccionado(s)`}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    </span>
                  </button>
                  
                  {openDropdowns.statuses && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                      <div className="p-2 border-b border-gray-200 bg-gray-50 sticky top-0">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={filterOptions.statuses.length > 0 && filters.status.length === filterOptions.statuses.length}
                            onChange={() => handleSelectAllChange('status', filterOptions.statuses.map(s => s))}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium">
                            {filterOptions.statuses.length > 0 && filters.status.length === filterOptions.statuses.length 
                              ? "Deseleccionar todos" 
                              : "Seleccionar todos"}
                          </span>
                        </label>
                      </div>
                      <div className="py-1 px-2">
                        {filterOptions.statuses.length === 0 ? (
                          <p className="text-sm text-gray-500 italic p-2">No hay estados disponibles</p>
                        ) : (
                          <div className="max-h-40 overflow-auto">
                            {filterOptions.statuses.map(status => (
                              <label key={status} className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 w-full">
                                <input
                                  type="checkbox"
                                  checked={filters.status.includes(status)}
                                  onChange={() => handleMultiSelectChange('status', status)}
                                  className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm truncate">{status}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Cumplimiento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cumplimiento
                  <span className="ml-1 text-xs text-gray-500">
                    ({filters.compliance.length} seleccionados)
                  </span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => toggleDropdown('compliances')}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-1 focus:ring-blue-500 flex justify-between items-center"
                  >
                    <span className="block truncate">
                      {filters.compliance.length === 0 ? 'Seleccionar cumplimiento' : 
                       filters.compliance.length === filterOptions.compliances.length ? 'Todos los cumplimientos' :
                       `${filters.compliance.length} cumplimiento(s) seleccionado(s)`}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    </span>
                  </button>
                  
                  {openDropdowns.compliances && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                      <div className="p-2 border-b border-gray-200 bg-gray-50 sticky top-0">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={filterOptions.compliances.length > 0 && filters.compliance.length === filterOptions.compliances.length}
                            onChange={() => handleSelectAllChange('compliance', filterOptions.compliances.map(c => c))}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium">
                            {filterOptions.compliances.length > 0 && filters.compliance.length === filterOptions.compliances.length 
                              ? "Deseleccionar todos" 
                              : "Seleccionar todos"}
                          </span>
                        </label>
                      </div>
                      <div className="py-1 px-2">
                        {filterOptions.compliances.length === 0 ? (
                          <p className="text-sm text-gray-500 italic p-2">No hay opciones de cumplimiento disponibles</p>
                        ) : (
                          <div className="max-h-40 overflow-auto">
                            {filterOptions.compliances.map(compliance => (
                              <label key={compliance} className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 w-full">
                                <input
                                  type="checkbox"
                                  checked={filters.compliance.includes(compliance)}
                                  onChange={() => handleMultiSelectChange('compliance', compliance)}
                                  className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm truncate">{compliance}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Botones de acción */}
            <div className="mt-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleResetFilters}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Limpiar filtros
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Aplicar filtros
              </button>
            </div>
          </form>
        </div>
      )}
      
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="animate-spin h-12 w-12 text-blue-600" />
          <span className="ml-2 text-lg text-gray-600">Cargando datos...</span>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Resumen general */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="font-semibold text-gray-700 text-lg mb-3">Tasa de Cumplimiento</h3>
              <p className="text-4xl font-bold text-green-600">{complianceRate}%</p>
              <p className="text-gray-500 mt-1">de cargas a tiempo</p>
            </div>
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="font-semibold text-gray-700 text-lg mb-3">Embarques Totales</h3>
              <p className="text-4xl font-bold text-blue-600">
                {totalShipments}
              </p>
              <p className="text-gray-500 mt-1">embarques analizados</p>
            </div>
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="font-semibold text-gray-700 text-lg mb-3">Embarques Con Retraso</h3>
              <p className="text-4xl font-bold text-red-600">
                {complianceData.delayed}
              </p>
              <p className="text-gray-500 mt-1">
                ({totalShipments > 0 ? ((complianceData.delayed / totalShipments) * 100).toFixed(1) : 0}%)
              </p>
            </div>
          </div>

          {/* Gráfico de distribución */}
          <div className="mt-8">
            <h3 className="font-semibold text-gray-700 text-xl mb-4">Distribución de cumplimiento</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} embarques`, ""]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Gráfico por clientes */}
          {getActiveTabData().length > 0 && (
            <div className="mt-8">
              <h3 className="font-semibold text-gray-700 text-xl mb-4">Cumplimiento por {getActiveTabTitle()}</h3>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={getActiveTabData().slice(0, 10)} // Mostrar los primeros 10 elementos
                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end"
                      height={80}
                      interval={0}
                    />
                    <YAxis 
                      yAxisId="left"
                      orientation="left"
                      stroke="#8884d8"
                      label={{ value: 'Cantidad de Entregas', angle: -90, position: 'insideLeft' }}
                    />
                    <YAxis 
                      yAxisId="right"
                      orientation="right"
                      stroke="#82ca9d"
                      label={{ value: 'Tasa de Cumplimiento (%)', angle: 90, position: 'insideRight' }}
                    />
                    <Tooltip />
                    <Legend verticalAlign="top" height={36} />
                    <Bar yAxisId="left" name="A Tiempo" dataKey="onTime" fill={COLORS.onTime} />
                    <Bar yAxisId="left" name="Con Retraso" dataKey="delayed" fill={COLORS.delayed} />
                    <Bar yAxisId="right" name="% Cumplimiento" dataKey="onTimeRate" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          
          {/* Tabla de detalles con pestañas */}
          {(complianceByClient.length > 0 || complianceByCarrier.length > 0 || complianceByFinalClient.length > 0) && (
            <div className="mt-8 overflow-x-auto">
              <div className="border-b border-gray-200">
                <h3 className="font-semibold text-gray-700 text-xl mb-4">Detalle de Cumplimiento</h3>
                <nav className="-mb-px flex space-x-8">
                  <button
                    className={`${
                      activeTab === 'client'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                    onClick={() => setActiveTab('client')}
                  >
                    Por Cliente
                  </button>
                  <button
                    className={`${
                      activeTab === 'carrier'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                    onClick={() => setActiveTab('carrier')}
                  >
                    Por Línea Transportista
                  </button>
                  <button
                    className={`${
                      activeTab === 'finalClient'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                    onClick={() => setActiveTab('finalClient')}
                  >
                    Por Cliente Final
                  </button>
                </nav>
              </div>
              
              {/* Contenido de la pestaña activa */}
              <div className="mt-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {getActiveTabTitle()}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        A Tiempo
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Con Retraso
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        % Cumplimiento
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getActiveTabData().length > 0 ? (
                      getActiveTabData().map((item, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.total}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.onTime}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.delayed}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-1 bg-gray-200 rounded-full h-2.5 w-full max-w-[150px]">                                <div 
                                  className="bg-green-600 h-2.5 rounded-full" 
                                  style={{ width: `${(item.onTimeRate !== undefined && item.onTimeRate !== null) ? item.onTimeRate : 0}%` }}
                                ></div>
                              </div>                              <span className="ml-3 text-sm text-gray-700">
                                {(item.onTimeRate !== undefined && item.onTimeRate !== null) ? item.onTimeRate.toFixed(1) : "0.0"}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                          No hay datos disponibles para este criterio
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Tabla de Embarques - Envolver en un div con overflow-x-auto */}
          <div className="mt-12">
            <h3 className="font-semibold text-gray-700 text-xl mb-4">Detalle de Embarques Analizados</h3>
            
            {/* Barra de búsqueda */}
            <div className="flex items-center mb-4 relative">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Buscar por referencia, truck, cliente o proveedor..."
                />
              </div>
            </div>
            
            {/* Tabla con contenedor responsivo */}
            <div className="overflow-x-auto shadow-md rounded-lg border border-gray-200">
              <div className="min-w-full inline-block align-middle">
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 table-fixed md:table-auto">
                    <thead className="bg-gray-50">
                      <tr>                        <th 
                          scope="col" 
                          className="cursor-pointer px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto"
                          onClick={() => requestSort('reference')}
                        >
                          <div className="flex items-center">
                            <span className="whitespace-nowrap">Referencia</span>
                            {sortConfig.key === 'reference' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="ml-1 h-4 w-4 flex-shrink-0" /> : 
                                <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="cursor-pointer px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto"
                          onClick={() => requestSort('truck')}
                        >
                          <div className="flex items-center">
                            <span className="whitespace-nowrap">TRUCK</span>
                            {sortConfig.key === 'truck' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="ml-1 h-4 w-4 flex-shrink-0" /> : 
                                <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="cursor-pointer px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto"
                          onClick={() => requestSort('vendor')}
                        >
                          <div className="flex items-center">
                            <span className="whitespace-nowrap">Cliente</span>
                            {sortConfig.key === 'vendor' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="ml-1 h-4 w-4 flex-shrink-0" /> : 
                                <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="cursor-pointer px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto"
                          onClick={() => requestSort('customer')}
                        >
                          <div className="flex items-center">
                            <span className="whitespace-nowrap">Cliente Final</span>
                            {sortConfig.key === 'customer' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="ml-1 h-4 w-4 flex-shrink-0" /> : 
                                <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="cursor-pointer px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto"
                          onClick={() => requestSort('Lt')}
                        >
                          <div className="flex items-center">
                            <span className="whitespace-nowrap">L. Transporte</span>
                            {sortConfig.key === 'Lt' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="ml-1 h-4 w-4 flex-shrink-0" /> : 
                                <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="cursor-pointer px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto"
                          onClick={() => requestSort('status')}
                        >
                          <div className="flex items-center">
                            <span className="whitespace-nowrap">Estado</span>
                            {sortConfig.key === 'status' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="ml-1 h-4 w-4 flex-shrink-0" /> : 
                                <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="cursor-pointer px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto"
                          onClick={() => requestSort('complianceStatus')}
                        >
                          <div className="flex items-center">
                            <span className="whitespace-nowrap">Cumplimiento</span>
                            {sortConfig.key === 'complianceStatus' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="ml-1 h-4 w-4 flex-shrink-0" /> : 
                                <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="cursor-pointer px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto"
                          onClick={() => requestSort('scheduledDate')}
                        >
                          <div className="flex items-center">
                            <span className="whitespace-nowrap">F. Programada</span>
                            {sortConfig.key === 'scheduledDate' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="ml-1 h-4 w-4 flex-shrink-0" /> : 
                                <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="cursor-pointer px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto"
                          onClick={() => requestSort('actualDate')}
                        >
                          <div className="flex items-center">
                            <span className="whitespace-nowrap">F. Real</span>
                            {sortConfig.key === 'actualDate' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="ml-1 h-4 w-4 flex-shrink-0" /> : 
                                <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="cursor-pointer px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto"
                          onClick={() => requestSort('delayTime')}
                        >
                          <div className="flex items-center">
                            <span className="whitespace-nowrap">T. Retraso</span>
                            {sortConfig.key === 'delayTime' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="ml-1 h-4 w-4 flex-shrink-0" /> : 
                                <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="cursor-pointer px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto"
                          onClick={() => requestSort('orderCount')}
                        >
                          <div className="flex items-center">
                            <span className="whitespace-nowrap">Órdenes</span>
                            {sortConfig.key === 'orderCount' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="ml-1 h-4 w-4 flex-shrink-0" /> : 
                                <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0" />
                            )}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentItems.length > 0 ? (
                        currentItems.map((shipment) => (                          <tr key={shipment.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 truncate max-w-[100px]">
                              <div className="truncate">{shipment.reference}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-[100px]">
                              <div className="truncate">{shipment.truck || 'N/A'}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-[120px]">
                              <div className="truncate">{shipment.vendor}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-[120px]">
                              <div className="truncate">{shipment.customer}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-[100px]">
                              <div className="truncate">{shipment.Lt}</div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full
                                ${shipment.status === 'ENTREGADO' ? 'bg-green-100 text-green-800' : 
                                 shipment.status === 'TRANSITO' ? 'bg-blue-100 text-blue-800' : 
                                 'bg-gray-100 text-gray-800'}`}
                              >
                                {shipment.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center">
                                {shipment.complianceStatus === 'onTime' ? (
                                  <CheckCircle className="h-5 w-5 text-green-500 mr-1 flex-shrink-0" />
                                ) : shipment.complianceStatus === 'delayed' ? (
                                  <XCircle className="h-5 w-5 text-red-500 mr-1 flex-shrink-0" />
                                ) : (
                                  <AlertCircle className="h-5 w-5 text-gray-400 mr-1 flex-shrink-0" />
                                )}
                                <span className={`text-xs ${
                                  shipment.complianceStatus === 'onTime' ? 'text-green-700' :
                                  shipment.complianceStatus === 'delayed' ? 'text-red-700' :
                                  'text-gray-600'
                                }`}>
                                  {shipment.complianceStatus === 'onTime' ? 'A Tiempo' :
                                   shipment.complianceStatus === 'delayed' ? 'Con Retraso' :
                                   'Pendiente'}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 max-w-[150px]">
                              {shipment.pickup_date ? (
                                formatDate(shipment.pickup_date)
                              ) : (
                                <span className="text-gray-500">Sin fecha</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 max-w-[150px]">
                              {shipment.actualDate ? (
                                formatDate(shipment.actualDate)
                              ) : (
                                <span className="text-gray-500">Sin fecha</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm max-w-[150px]">
                              {shipment.delayTime ? (
                                <span className="text-red-600 font-medium">{formatDelayTime(shipment.delayTime)}</span>
                              ) : (
                                <span className="text-gray-500">Sin retraso</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-center text-gray-500">
                              {shipment.orderCount}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="10" className="px-4 py-3 text-center text-sm text-gray-500">
                            No se encontraron embarques que coincidan con los criterios de búsqueda
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            {/* Paginación */}
            {filteredShipments.length > 0 && (
              <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 mt-4">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage < totalPages ? currentPage + 1 : totalPages)}
                    disabled={currentPage === totalPages}
                    className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{indexOfFirstItem + 1}</span> a <span className="font-medium">{Math.min(indexOfLastItem, filteredShipments.length)}</span> de <span className="font-medium">{filteredShipments.length}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span className="sr-only">Anterior</span>
                        <ChevronUp className="h-5 w-5 transform -rotate-90" />
                      </button>
                      
                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        // Mostrar páginas alrededor de la actual
                        let pageNum;
                        if (totalPages <= 5) {
                          // Si hay 5 o menos páginas, mostrarlas todas
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          // Si estamos cerca del inicio
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          // Si estamos cerca del final
                          pageNum = totalPages - 4 + i;
                        } else {
                          // Si estamos en el medio
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={i}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border ${currentPage === pageNum ? 'bg-blue-50 border-blue-500 text-blue-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'} text-sm font-medium`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(currentPage < totalPages ? currentPage + 1 : totalPages)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span className="sr-only">Siguiente</span>
                        <ChevronDown className="h-5 w-5 transform -rotate-90" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PickupComplianceDashboard;